/**
 * DeleteAccountModal Component
 * Confirmation modal for permanently deleting an account
 * WP-ACCT-2: Account Deletion
 */
import { useState, useEffect } from 'react';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import { formatCurrency } from '@/utils/format';
import { useDeleteAccount } from '@/hooks/useAccounts';
import { ApiClientError } from '@/services/api';
import { toast } from 'sonner';
import type { AccountWithConnection } from '@/services/accounts';

export interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  account: AccountWithConnection | null;
}

export default function DeleteAccountModal({
  isOpen,
  onClose,
  account,
}: DeleteAccountModalProps) {
  const [error, setError] = useState<string | null>(null);
  const deleteMutation = useDeleteAccount();

  // Reset error when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!account) return;

    setError(null);

    deleteMutation.mutate(account.id, {
      onSuccess: () => {
        toast.success(`Account "${account.name}" has been deleted`);
        handleClose();
      },
      onError: (err) => {
        if (err instanceof ApiClientError) {
          setError(err.detail);
        } else {
          setError('Failed to delete account. Please try again.');
        }
      },
    });
  };

  if (!isOpen || !account) return null;

  // Calculate display balance (negative for liabilities)
  const displayBalance =
    account.type === 'credit' || account.type === 'loan'
      ? -Math.abs(account.balance)
      : account.balance;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Danger icon */}
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 mb-4">
          <TrashIcon className="h-6 w-6 text-red-600" />
        </div>

        <h2
          id="delete-account-modal-title"
          className="text-lg font-semibold text-gray-900 text-center mb-2"
        >
          Delete Account
        </h2>

        <p className="text-sm text-gray-600 text-center mb-4">
          Are you sure you want to permanently delete{' '}
          <span className="font-medium text-gray-900">{account.name}</span>?
        </p>

        {/* Account details */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">Last Balance</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(displayBalance, account.currency)}
            </span>
          </div>
          {account.connection?.institutionName && (
            <div className="flex justify-between items-center text-sm mt-2">
              <span className="text-gray-600">Institution</span>
              <span className="font-medium text-gray-900">
                {account.connection.institutionName}
              </span>
            </div>
          )}
        </div>

        {/* Warning explanation */}
        <div className="bg-red-50 border border-red-100 rounded-lg p-3 mb-4">
          <p className="text-sm text-red-800">
            <strong>This action cannot be undone.</strong> The account, all its
            transactions, and any associated holdings will be permanently removed.
          </p>
        </div>

        {error && (
          <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={deleteMutation.isPending}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleConfirm}
            isLoading={deleteMutation.isPending}
            className="flex-1"
          >
            Delete Account
          </Button>
        </div>
      </div>
    </div>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}
